import React, { ReactNode } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { EASE_OUT } from './motion';

// The trigger lives on the (unclipped) wrapper; the clipped children only
// carry variants. An element hidden behind its own mask never intersects
// the viewport, so observing the child directly would never fire.
function trigger(show: boolean | undefined) {
  return show === undefined
    ? { initial: 'hidden', whileInView: 'visible', viewport: { once: true, margin: '0px 0px -8% 0px' } }
    : { initial: 'hidden', animate: show ? 'visible' : 'hidden' };
}

type RevealProps = {
  children: ReactNode;
  delay?: number;
  /** Controlled mode. Leave undefined to reveal when scrolled into view. */
  show?: boolean;
  className?: string;
};

/** A line of text that slides up from behind a mask. */
export function Reveal({ children, delay = 0, show, className = '' }: RevealProps) {
  const reduce = useReducedMotion();
  return (
    <motion.span className={`reveal-mask ${className}`} {...trigger(show)}>
      <motion.span
        variants={{
          hidden: reduce ? { opacity: 0 } : { y: '110%' },
          visible: {
            y: '0%',
            opacity: 1,
            transition: { duration: reduce ? 0.4 : 1.1, ease: EASE_OUT, delay },
          },
        }}
      >
        {children}
      </motion.span>
    </motion.span>
  );
}

type SplitProps = {
  text: string;
  show?: boolean;
  delay?: number;
  stagger?: number;
  className?: string;
};

/** Letters rise one after another. Screen readers get the plain text. */
export function SplitChars({ text, show, delay = 0, stagger = 0.04, className = '' }: SplitProps) {
  const reduce = useReducedMotion();
  return (
    <motion.span className={className} {...trigger(show)}>
      <span className="visually-hidden">{text}</span>
      {Array.from(text).map((char, i) => (
        <span
          key={i}
          aria-hidden="true"
          style={{ display: 'inline-block', overflow: 'hidden', verticalAlign: 'top' }}
        >
          <motion.span
            style={{ display: 'inline-block', whiteSpace: 'pre' }}
            variants={{
              hidden: reduce ? { opacity: 0 } : { y: '105%', rotate: 6 },
              visible: {
                y: '0%',
                rotate: 0,
                opacity: 1,
                transition: { duration: 1.2, ease: EASE_OUT, delay: delay + i * stagger },
              },
            }}
          >
            {char}
          </motion.span>
        </span>
      ))}
    </motion.span>
  );
}

/** Text that swaps to a serif italic twin on hover, rolling vertically. */
export function RollText({ children, className = '' }: { children: string; className?: string }) {
  return (
    <span className={`roll ${className}`}>
      <span className="roll__a">{children}</span>
      <span className="roll__b serif" aria-hidden="true">
        {children}
      </span>
    </span>
  );
}
